import {
  useCallback,
  useEffect,
  useState,
  type ReactNode
} from "react";
import { api } from "../api";
import { MigrationsPage } from "./MigrationsPage";
import type { PanelUser, ServerNode } from "../types";

type RemoteAccount = {
  username: string;
  domains: string[];
  primaryDomain?: string;
  home?: string;
  panel?: string;
};

type ImportMode = "webuzo" | "cpanel";

/**
 * Softaculous-style Import hub: Webuzo remote + cPanel remote (+ archive backup).
 */
export function ImportPage({
  mode,
  accounts,
  servers = [],
  currentUser,
  onRefreshAccounts
}: {
  mode: ImportMode;
  accounts: PanelUser[];
  servers?: ServerNode[];
  currentUser: PanelUser;
  onRefreshAccounts: () => Promise<void>;
}) {
  const [tab, setTab] = useState<string>(
    mode === "webuzo" ? "import" : "multi"
  );
  const [host, setHost] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [sshPort, setSshPort] = useState("22");
  const [method, setMethod] = useState<"rsync" | "api">("rsync");
  const [sshUser, setSshUser] = useState(
    mode === "webuzo" ? "root" : "root"
  );
  const [enduser, setEnduser] = useState("");
  const [liveTransfer, setLiveTransfer] = useState(false);
  const [importIps, setImportIps] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [accountsFound, setAccountsFound] = useState<RemoteAccount[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [scanMeta, setScanMeta] = useState<Record<string, unknown> | null>(
    null
  );
  const [logs, setLogs] = useState<
    { id: string; type: string; summary?: string; updatedAt: string }[]
  >([]);
  const [scanLogs, setScanLogs] = useState<string[]>([]);

  // Reset form when switching Webuzo/cPanel mode
  useEffect(() => {
    setTab(mode === "webuzo" ? "import" : "multi");
    setAccountsFound([]);
    setSelected({});
    setScanMeta(null);
    setError("");
    setMsg("");
    setSshUser("root");
  }, [mode]);

  const loadLogs = useCallback(async () => {
    try {
      const res = await api<{
        items?: {
          id: string;
          type: string;
          summary?: string;
          updatedAt: string;
        }[];
      }>("/api/v1/migrations/remote/logs");
      setLogs(res.items || []);
    } catch {
      setLogs([]);
    }
  }, []);

  useEffect(() => {
    if (tab === "logs") void loadLogs();
  }, [tab, loadLogs]);

  const scan = async () => {
    if (!host.trim()) {
      setError(
        mode === "webuzo"
          ? "Server IP is required"
          : "cPanel Server Address is required"
      );
      return;
    }
    if (mode === "cpanel" && tab === "enduser" && !enduser.trim() && !sshUser) {
      setError("User Name is required");
      return;
    }
    setBusy(true);
    setError("");
    setMsg("");
    setAccountsFound([]);
    setSelected({});
    try {
      const res = await api<{
        ok: boolean;
        result: {
          accounts?: RemoteAccount[];
          accountCount?: number;
          logs?: string[];
          note?: string;
          hasCpanel?: boolean;
          hasWebuzo?: boolean;
          scanId?: string;
        };
      }>("/api/v1/migrations/remote/scan", {
        method: "POST",
        body: JSON.stringify({
          kind: mode,
          host: host.trim(),
          port: Number(sshPort) || 22,
          sshUser:
            mode === "cpanel" && tab === "enduser"
              ? enduser.trim() || sshUser
              : sshUser || "root",
          password,
          method,
          enduser:
            mode === "cpanel" && tab === "enduser"
              ? enduser.trim() || undefined
              : undefined
        })
      });
      const list = res.result?.accounts || [];
      setAccountsFound(list);
      setScanMeta(res.result as Record<string, unknown>);
      setScanLogs(res.result?.logs || []);
      setMsg(
        `Scan complete — ${list.length} account(s) found` +
          (res.result?.note ? `. ${res.result.note}` : "")
      );
      const next: Record<string, boolean> = {};
      for (const a of list) next[a.username] = true;
      setSelected(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const startImport = async () => {
    const usernames = Object.entries(selected)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (!usernames.length) {
      setError("Select at least one account to import");
      return;
    }
    if (
      !confirm(
        `Start Direct Transfer for ${usernames.length} account(s) from ${host}?`
      )
    ) {
      return;
    }
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const res = await api<{
        ok: boolean;
        result: {
          jobId?: string;
          ok?: boolean;
          note?: string;
          steps?: { name: string; ok: boolean; detail?: string }[];
        };
      }>("/api/v1/migrations/remote/import", {
        method: "POST",
        body: JSON.stringify({
          kind: mode,
          host: host.trim(),
          port: Number(sshPort) || 22,
          sshUser: sshUser || "root",
          password,
          usernames,
          liveTransfer,
          importIps
        })
      });
      const failed =
        res.result?.steps?.filter((s) => !s.ok).map((s) => s.name) || [];
      setMsg(
        res.result?.note ||
          (res.result?.ok
            ? `Import staged (job ${res.result.jobId || "—"})`
            : `Import finished with issues: ${failed.join(", ")}`)
      );
      void loadLogs();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const title =
    mode === "webuzo" ? "Import From Webuzo" : "Import From cPanel";
  const cpanelTabs =
    mode === "cpanel"
      ? [
          { id: "multi", label: "Import multiple users" },
          { id: "enduser", label: "Import with enduser credentials" },
          { id: "backup", label: "Import from backup" },
          { id: "live", label: "Disable Live Transfer" },
          { id: "logs", label: "Logs" }
        ]
      : [
          { id: "import", label: "Import Webuzo" },
          { id: "logs", label: "Logs" }
        ];

  if (mode === "cpanel" && tab === "backup") {
    return (
      <div className="imp-page">
        <Header title={title} mode={mode} />
        <TabBar tabs={cpanelTabs} tab={tab} setTab={setTab} />
        <div className="imp-card">
          <p className="card-meta" style={{ marginBottom: 12 }}>
            Upload a cPanel full backup, cpmove, or RealPanel package and
            import into an existing account.
          </p>
          <MigrationsPage
            accounts={accounts}
            servers={servers}
            currentUser={currentUser}
            onRefreshAccounts={onRefreshAccounts}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="imp-page">
      <Header title={title} mode={mode} />

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="imp-card">
        <TabBar tabs={cpanelTabs} tab={tab} setTab={setTab} />

        {tab === "logs" ? (
          <div className="imp-body">
            <div className="imp-logs-head">
              <h3>Import logs</h3>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => void loadLogs()}
              >
                Refresh
              </button>
            </div>
            {scanLogs.length ? (
              <div className="imp-scan-logs mono">
                {scanLogs.map((l, i) => (
                  <div key={i}>{l}</div>
                ))}
              </div>
            ) : null}
            {logs.length === 0 ? (
              <p className="card-meta faint">No remote import logs yet.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Type</th>
                    <th>Summary</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id + l.updatedAt}>
                      <td className="mono">{l.id}</td>
                      <td>{l.type}</td>
                      <td>{l.summary || "—"}</td>
                      <td className="faint mono" style={{ fontSize: 12 }}>
                        {l.updatedAt}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : tab === "live" ? (
          <div className="imp-body">
            <h3 className="imp-section-title">Live Transfer</h3>
            <p className="card-meta">
              Live Transfer keeps the remote account online during rsync
              (zero-downtime style). Disable it if you prefer a final
              offline cutover only.
            </p>
            <label className="imp-check">
              <input
                type="checkbox"
                checked={liveTransfer}
                onChange={(e) => setLiveTransfer(e.target.checked)}
              />
              <span>
                Enable Live Transfer (zero-downtime migration)
                <small>
                  When enabled, rsync runs while the source stays live; do a
                  final sync after freeze for cutover.
                </small>
              </span>
            </label>
            <p className="card-meta faint" style={{ marginTop: 16 }}>
              This preference is applied on the next <strong>Import
              multiple users</strong> transfer.
            </p>
          </div>
        ) : (
          <div className="imp-body">
            <div className="imp-grid">
              {mode === "webuzo" ? (
                <>
                  <Field
                    label="Server IP"
                    tip="Remote Webuzo server IPv4 or hostname"
                  >
                    <input
                      value={host}
                      onChange={(e) => setHost(e.target.value)}
                      placeholder="203.0.113.10"
                      autoComplete="off"
                    />
                  </Field>
                  <Field
                    label="Server Root Password (Remote)"
                    tip="Root password for SSH on the remote Webuzo host"
                  >
                    <div className="imp-pass">
                      <input
                        type={showPass ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="imp-eye"
                        onClick={() => setShowPass((v) => !v)}
                        aria-label="Toggle password"
                      >
                        {showPass ? "Hide" : "Show"}
                      </button>
                    </div>
                  </Field>
                  <div className="imp-field">
                    <span className="imp-label">Migration Method</span>
                    <div className="imp-radios">
                      <label>
                        <input
                          type="radio"
                          checked={method === "rsync"}
                          onChange={() => setMethod("rsync")}
                        />
                        Direct Transfer (rsync)
                      </label>
                      <label>
                        <input
                          type="radio"
                          checked={method === "api"}
                          onChange={() => setMethod("api")}
                        />
                        API Method
                      </label>
                    </div>
                  </div>
                  <Field label="SSH Port" tip="Default 22">
                    <input
                      value={sshPort}
                      onChange={(e) => setSshPort(e.target.value)}
                    />
                  </Field>
                </>
              ) : (
                <>
                  <Field
                    label="cPanel Server Address (Required)"
                    tip="Hostname or IP of the source cPanel server"
                  >
                    <input
                      value={host}
                      onChange={(e) => setHost(e.target.value)}
                      placeholder="cpanel.example.com"
                      autoComplete="off"
                    />
                  </Field>
                  <Field
                    label={
                      tab === "enduser"
                        ? "User Name (Required)"
                        : "SSH / WHM User"
                    }
                    tip={
                      tab === "enduser"
                        ? "cPanel account username"
                        : "Usually root for multi-user import"
                    }
                  >
                    <input
                      value={tab === "enduser" ? enduser : sshUser}
                      onChange={(e) =>
                        tab === "enduser"
                          ? setEnduser(e.target.value)
                          : setSshUser(e.target.value)
                      }
                      placeholder={tab === "enduser" ? "byking" : "root"}
                      autoComplete="off"
                    />
                  </Field>
                  <Field label="Password">
                    <div className="imp-pass">
                      <input
                        type={showPass ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="imp-eye"
                        onClick={() => setShowPass((v) => !v)}
                      >
                        {showPass ? "Hide" : "Show"}
                      </button>
                    </div>
                  </Field>
                  <div className="imp-field">
                    <span className="imp-label">Migration Method</span>
                    <div className="imp-radios">
                      <label>
                        <input
                          type="radio"
                          checked={method === "rsync"}
                          onChange={() => setMethod("rsync")}
                        />
                        Direct Transfer (rsync)
                      </label>
                      <label>
                        <input
                          type="radio"
                          checked={method === "api"}
                          onChange={() => setMethod("api")}
                        />
                        API Method
                      </label>
                    </div>
                  </div>
                  <Field label="SSH Port" tip="Default 22">
                    <input
                      value={sshPort}
                      onChange={(e) => setSshPort(e.target.value)}
                    />
                  </Field>
                  <div className="imp-field">
                    <span className="imp-label">Live Transfer</span>
                    <label className="imp-check">
                      <input
                        type="checkbox"
                        checked={liveTransfer}
                        onChange={(e) => setLiveTransfer(e.target.checked)}
                      />
                      <span>
                        Enable Live Transfer (zero-downtime migration)
                      </span>
                    </label>
                  </div>
                  <div className="imp-field imp-field-full">
                    <span className="imp-label">Import IPs</span>
                    <label className="imp-check">
                      <input
                        type="checkbox"
                        checked={importIps}
                        onChange={(e) => setImportIps(e.target.checked)}
                      />
                      <span>
                        Import cPanel server IPs (as inactive)
                      </span>
                    </label>
                  </div>
                </>
              )}
            </div>

            <div className="imp-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={() => void scan()}
              >
                {busy
                  ? "Scanning…"
                  : mode === "webuzo"
                    ? "Scan Remote Server"
                    : "Scan cPanel Server"}
              </button>
            </div>

            <p className="imp-note">
              <strong>Note:</strong> This utility imports data from another{" "}
              {mode === "webuzo" ? "Webuzo" : "cPanel"} instance over SSH
              using Direct Transfer (rsync) or an API path. Ensure port{" "}
              {sshPort || "22"} is open and the agent host has{" "}
              <span className="mono">ssh</span>
              {method === "rsync" ? (
                <>
                  {" "}
                  + <span className="mono">rsync</span> +{" "}
                  <span className="mono">sshpass</span>
                </>
              ) : null}
              .
            </p>

            {accountsFound.length > 0 ? (
              <div className="imp-results">
                <div className="imp-results-head">
                  <h3>
                    Accounts found ({accountsFound.length})
                    {scanMeta?.hasCpanel != null ? (
                      <span className="faint" style={{ fontWeight: 500 }}>
                        {" "}
                        · remote cPanel:{" "}
                        {scanMeta.hasCpanel ? "yes" : "no"}
                        {scanMeta.hasWebuzo != null
                          ? ` · Webuzo: ${scanMeta.hasWebuzo ? "yes" : "no"}`
                          : ""}
                      </span>
                    ) : null}
                  </h3>
                  <div className="imp-results-actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        const next: Record<string, boolean> = {};
                        for (const a of accountsFound)
                          next[a.username] = true;
                        setSelected(next);
                      }}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={busy}
                      onClick={() => void startImport()}
                    >
                      {busy ? "Importing…" : "Start Import"}
                    </button>
                  </div>
                </div>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th />
                        <th>Username</th>
                        <th>Primary domain</th>
                        <th>Domains</th>
                        <th>Home</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accountsFound.map((a) => (
                        <tr key={a.username}>
                          <td>
                            <input
                              type="checkbox"
                              checked={Boolean(selected[a.username])}
                              onChange={(e) =>
                                setSelected((p) => ({
                                  ...p,
                                  [a.username]: e.target.checked
                                }))
                              }
                            />
                          </td>
                          <td className="mono">
                            <strong>{a.username}</strong>
                          </td>
                          <td>{a.primaryDomain || "—"}</td>
                          <td className="faint" style={{ fontSize: 12 }}>
                            {(a.domains || []).join(", ") || "—"}
                          </td>
                          <td className="mono faint" style={{ fontSize: 12 }}>
                            {a.home || `/home/${a.username}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function Header({ title, mode }: { title: string; mode: ImportMode }) {
  return (
    <div className="imp-hero">
      <span className="imp-hero-icon" aria-hidden>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <div>
        <h1>{title}</h1>
        <p>
          {mode === "webuzo"
            ? "Pull accounts from a remote Webuzo server"
            : "Pull accounts from a remote cPanel server"}
        </p>
      </div>
    </div>
  );
}

function TabBar({
  tabs,
  tab,
  setTab
}: {
  tabs: { id: string; label: string }[];
  tab: string;
  setTab: (t: string) => void;
}) {
  return (
    <div className="imp-tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          className={`imp-tab ${tab === t.id ? "is-active" : ""}`}
          onClick={() => setTab(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Field({
  label,
  tip,
  children
}: {
  label: string;
  tip?: string;
  children: ReactNode;
}) {
  return (
    <label className="imp-field">
      <span className="imp-label">
        {label}
        {tip ? (
          <span className="imp-tip" title={tip}>
            i
          </span>
        ) : null}
      </span>
      {children}
    </label>
  );
}
